import { useEffect, useRef, useState } from "react";

import { useToast } from "@smart/bridge-intake-components-dom";
import { loadLocale } from "@smart/itops-locale-dom";
import { Modal, Table } from "@smart/itops-sb-design-system-dom";
import { isNotNullOrUndefined, pluralise } from "@smart/itops-utils-basic";
import {
  useDownloadSubmissions,
  useLazyLoadMatterPermissions,
  useLoadWebEnquiries,
} from "@smart/manage-gql-client-dom";

import { Empty } from "./empty";
import { FlaggedSubmissionModal } from "./flagged-submission-modal";
import { NoContactModal } from "./no-contact-modal";
import { GqlSubmissionSummary } from "../../types";
import { useFilterSync } from "../filter-sync-context";
import { useSubmissionsWithMatter, useWebEnquiriesColumns } from "../hooks";

export const WebEnquiries = ({
  openNoMatterSubmissionId,
}: {
  openNoMatterSubmissionId?: string;
}) => {
  const { terms } = loadLocale();
  const [noContactRow, setNoContactRow] = useState<GqlSubmissionSummary>();
  const [flaggedRow, setFlaggedRow] = useState<GqlSubmissionSummary>();
  const [tableKey, setTableKey] = useState(0);
  const filterSync = useFilterSync();
  const { columns, select, sort, timeFilter, setTimeFilter, onRowClick } =
    useWebEnquiriesColumns({
      onNoMatterRowClick: setNoContactRow,
      onFlaggedRowClick: setFlaggedRow,
    });

  const { setToasts } = useToast();
  const { loading, result, refetching, refetch } = useLoadWebEnquiries(
    timeFilter,
    { reloadOnVisible: true, pollInterval: 180000 },
  );
  const { submissionsWithMatter: webEnquiries } = useSubmissionsWithMatter({
    submissions: result,
    loading,
    withMissingContactDetailsSet: true,
  });
  const downloadSubmissions = useDownloadSubmissions();
  const { getPermissions } = useLazyLoadMatterPermissions();
  const [noPermissionsMatterIds, setNoPermissionsMatterIds] = useState<
    string[]
  >([]);
  const [isRefetching, setIsRefetching] = useState(false);
  const selectedSubmissions = useRef<GqlSubmissionSummary[]>([]);

  const exportSubmissions = async () => {
    const submissionsToExport = selectedSubmissions.current.filter(
      (s) =>
        !s.values.matterId ||
        !noPermissionsMatterIds.includes(s.values.matterId),
    );
    try {
      await downloadSubmissions(submissionsToExport);
      setToasts([
        {
          palette: "success",
          text: "Export complete! Your file has been downloaded.",
        },
      ]);
    } catch (error) {
      console.error(error);
      setToasts([
        {
          palette: "destructive",
          text: "We couldn’t export your file. Check your connection and try again.",
        },
      ]);
    }
  };

  const onBatchAction = async (selected: GqlSubmissionSummary[]) => {
    selectedSubmissions.current = selected;

    const selectedMatterIds = [
      ...new Set(
        selected.map((s) => s.values.matterId).filter(isNotNullOrUndefined),
      ),
    ];
    if (selectedMatterIds.length) {
      const matterPermissions = await getPermissions({
        matterIds: selectedMatterIds,
      });
      const noPermissions = matterPermissions?.filter((p) => !p.canAccess);
      if (noPermissions?.length) {
        setNoPermissionsMatterIds(noPermissions.map((p) => p.matterId));
        return;
      }
    }

    await exportSubmissions();
  };

  useEffect(() => {
    if (timeFilter) refetch();
  }, [timeFilter]);

  useEffect(() => {
    if (filterSync?.tabKey !== "web-enquiries") return;

    setTimeFilter(filterSync.period, filterSync.range);
    setTableKey(filterSync.nonce);
  }, [filterSync, setTimeFilter]);

  useEffect(() => {
    if (openNoMatterSubmissionId) {
      const submission = webEnquiries.find(
        (s) => s.uri === openNoMatterSubmissionId,
      );
      if (submission && !submission.matter?.id) {
        onRowClick(submission);
      }
    }
  }, [openNoMatterSubmissionId]);

  const handleClose = async () => {
    setIsRefetching(true);
    await refetch();
    setNoContactRow(undefined);
    setFlaggedRow(undefined);
    setIsRefetching(false);
  };

  return (
    <>
      <Table
        key={`intake-web-enquiries-${tableKey}`}
        id="intake-web-enquiries"
        data={webEnquiries}
        columns={columns}
        updating={loading || refetching}
        loadingRows={loading ? 3 : undefined}
        getRowId={(row) => row.uri}
        select={select}
        sort={sort}
        globalFilter={undefined}
        empty={<Empty message={`No web ${terms.enquiry.plural} found`} />}
        multiSelect
        onRowClick={onRowClick}
        enableColumnFilters
        className="rounded-b-none"
        fixedHeadings
        batchActions={[
          {
            text: "Export",
            onClick: onBatchAction,
          },
        ]}
      />
      <Modal
        header={{
          icon: "solidTriangleExclamation",
          iconBgColor: "bg-yellow-450",
          text: `Insufficient Permissions (${noPermissionsMatterIds.length})`,
        }}
        open={!!noPermissionsMatterIds.length}
        onClose={() => setNoPermissionsMatterIds([])}
        footer={{
          buttons: [
            {
              text: "Cancel",
              type: "reset",
              variant: "secondarySubtle",
              onClick: () => setNoPermissionsMatterIds([]),
            },
            {
              text: "Yes, export",
              onClick: async () => {
                setNoPermissionsMatterIds([]);
                await exportSubmissions();
              },
              variant: "primaryDefault",
            },
          ],
        }}
        size="default"
        closeOptions={{ clickOutside: false }}
      >
        <p>{`You do not have the necessary permissions for ${noPermissionsMatterIds.length} ${pluralise(noPermissionsMatterIds, "matter", "of the selected matters")}. ${pluralise(noPermissionsMatterIds, "This")} ${pluralise(noPermissionsMatterIds, "form")} will not be exported.`}</p>
        <p className="font-semibold">
          {`Do you want to continue exporting without ${pluralise(noPermissionsMatterIds, "it", "them")}?`}
        </p>
      </Modal>
      {noContactRow && !noContactRow.matter?.id && (
        <NoContactModal
          formUri={noContactRow.form.uri}
          submissionUri={noContactRow.uri}
          onClose={handleClose}
          isRefetching={isRefetching}
        />
      )}
      {flaggedRow && flaggedRow.values.verificationStatus === "flagged" && (
        <FlaggedSubmissionModal
          formUri={flaggedRow.form.uri}
          submissionUri={flaggedRow.uri}
          verificationDetails={flaggedRow.values.verificationDetails}
          onClose={handleClose}
          isRefetching={isRefetching}
        />
      )}
    </>
  );
};
